'use client'

import type { TFunction } from 'i18next'

import * as React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { filter, slice } from 'lodash/fp'
import {
	ArrowRightIcon,
	CircleDollarSignIcon,
	CornerDownLeftIcon,
	LaptopIcon,
	MoonIcon,
	NetworkIcon,
	PlusCircleIcon,
	SunIcon,
} from 'lucide-react'
import { useTheme } from 'next-themes'
import { useTranslation } from 'react-i18next'

import { useOptimizationGroupsQuery } from '@/entities/optimization-group'
import {
	EngineBadge,
	getPricingCampaignEngine,
	usePricingCampaignsQuery,
} from '@/entities/pricing-campaign'

import { Button } from '@/shared/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandList,
	CommandSeparator,
} from '@/shared/components/ui/command'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/shared/components/ui/dialog'
import { Kbd, KbdGroup } from '@/shared/components/ui/kbd'
import { Separator } from '@/shared/components/ui/separator'
import { Spinner } from '@/shared/components/ui/spinner'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { cn } from '@/shared/lib/utils'

import { CommandMenuItem } from './CommandMenuItem'
import { ResetOptimizationGroups } from './ResetOptimizationGroups'

const getMenuItems = (t: TFunction) => [
	{
		label: t('menu.og', { defaultValue: 'Optimization groups' }),
		href: '/p/og',
	},
	{
		label: t('menu.pricing_campaigns', { defaultValue: 'Pricing campaigns' }),
		href: '/p/pc',
	},
	{
		label: t('menu.promo'),
		href: '/p/promo',
	},
	{
		label: t('app.products'),
		href: '/p/products',
	},
	{
		label: t('app.anchoring_management.page.title', {
			defaultValue: 'Anchor management',
		}),
		href: '/p/anchor-management',
	},
	{
		label: t('templates', { defaultValue: 'Templates' }),
		href: '/p/templates',
	},
	{
		label: t('main_dashboard_title'),
		href: '/p/dashboard/main',
	},
	{
		label: t('ai_dashboard_title'),
		href: '/p/dashboard/ai',
	},
	{
		label: t('saved_reports.page.title'),
		href: '/p/dashboard/reports',
	},
]

export function CommandMenu() {
	const { t } = useTranslation()

	const menuItems = getMenuItems(t)

	const { showModal } = useModals()
	const { setTheme } = useTheme()
	const navigate = useNavigate()
	const [open, setOpen] = React.useState(false)
	const [search, setSearch] = React.useState('')
	const [selectedType, setSelectedType] = React.useState<
		'command' | 'page' | 'pc' | 'og' | 'theme' | null
	>(null)
	const [copyPayload, setCopyPayload] = React.useState('')
	const searchTimeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined)
	const lastTrackedQueryRef = React.useRef<string>('')
	const trackSearchQuery = React.useCallback((query: string) => {
		const trimmedQuery = query.trim()

		// Only track if the query is different from the last tracked query and has content.
		if (trimmedQuery && trimmedQuery !== lastTrackedQueryRef.current) {
			lastTrackedQueryRef.current = trimmedQuery
		}
	}, [])
	const handleSearchChange = React.useCallback(
		(value: string) => {
			// Clear existing timeout.
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}

			// Set new timeout to debounce both search and tracking.
			searchTimeoutRef.current = setTimeout(() => {
				setSearch(value)
				trackSearchQuery(value)
			}, 500)
		},
		[setSearch, trackSearchQuery]
	)
	React.useEffect(
		() => () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}
		},
		[]
	)
	const handlePageHighlight = React.useCallback(() => {
		setSelectedType('page')
		setCopyPayload('')
	}, [setSelectedType, setCopyPayload])
	const runCommand = React.useCallback((command: () => unknown) => {
		setOpen(false)
		command()
	}, [])
	const getFilteredData = <T extends { name: string }>(data: T[]) =>
		slice(
			0,
			5,
			filter(
				(item) =>
					item.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
				data
			)
		)
	const pricingCampaignsQuery = usePricingCampaignsQuery(
		{},
		{
			enabled: open,
			select: getFilteredData,
		}
	)
	const optimizationGroupsQuery = useOptimizationGroupsQuery({
		enabled: open,
		select: getFilteredData,
	})
	React.useEffect(() => {
		const down = (e: KeyboardEvent) => {
			if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || e.key === '/') {
				if (
					(e.target instanceof HTMLElement && e.target.isContentEditable) ||
					e.target instanceof HTMLInputElement ||
					e.target instanceof HTMLTextAreaElement ||
					e.target instanceof HTMLSelectElement
				) {
					return
				}

				e.preventDefault()
				setOpen((open) => !open)
			}

			if (e.key === 'c' && (e.metaKey || e.ctrlKey)) {
				runCommand(() => {})
			}
		}

		document.addEventListener('keydown', down)

		return () => document.removeEventListener('keydown', down)
	}, [copyPayload, runCommand, selectedType])
	const isLoading =
		pricingCampaignsQuery.isLoading || optimizationGroupsQuery.isLoading

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button
					variant='secondary'
					className={cn(
						'mb-4',
						'group-data-[collapsible=icon]:hidden',
						'relative h-8 w-full justify-start border bg-input/30 pl-3 font-medium text-foreground text-placeholder shadow-none sm:pr-12'
					)}
					onClick={() => setOpen(true)}
				>
					<span className='inline-flex'>Search...</span>
					<div className='absolute top-1.5 right-1.5 hidden gap-1 sm:flex'>
						<KbdGroup>
							<Kbd className='border'>⌘</Kbd>
							<Kbd className='border'>K</Kbd>
						</KbdGroup>
					</div>
				</Button>
			</DialogTrigger>
			<DialogContent className='rounded-xl border-none bg-clip-padding p-2 pb-11 shadow-2xl ring-4 ring-neutral-200/80 dark:bg-neutral-900 dark:ring-neutral-800'>
				<DialogHeader className='sr-only'>
					<DialogTitle>Search...</DialogTitle>
					<DialogDescription>Search for a command to run...</DialogDescription>
				</DialogHeader>
				<Command
					className='rounded-none bg-transparent **:data-[slot=command-input]:!h-9 **:data-[slot=command-input]:py-0 **:data-[slot=command-input-wrapper]:mb-0 **:data-[slot=command-input-wrapper]:!h-9 **:data-[slot=command-input-wrapper]:rounded-md **:data-[slot=command-input-wrapper]:border **:data-[slot=command-input-wrapper]:border-input **:data-[slot=command-input-wrapper]:bg-input/50'
					filter={(value, search, keywords) => {
						handleSearchChange(search)
						const extendValue = value + ' ' + (keywords?.join(' ') || '')
						if (extendValue.toLowerCase().includes(search.toLowerCase())) {
							return 1
						}

						return 0
					}}
				>
					<div className='relative'>
						<CommandInput placeholder='Type a command or search...' />
						{isLoading && (
							<div className='pointer-events-none absolute top-1/2 right-3 z-10 flex -translate-y-1/2 items-center justify-center'>
								<Spinner className='size-4 text-muted-foreground' />
							</div>
						)}
					</div>
					<CommandList className='no-scrollbar min-h-80 scroll-pt-2 scroll-pb-1.5'>
						<CommandEmpty className='py-12 text-center text-sm text-muted-foreground'>
							{isLoading ? 'Searching...' : 'No results found.'}
						</CommandEmpty>
						<CommandGroup heading='Commands'>
							<CommandMenuItem
								key='create-pricing-campaign'
								value='Create pricing campaign'
								keywords={['Create pricing campaign', 'pricing campaign']}
								onHighlight={() => setSelectedType('command')}
								onSelect={() => {
									// TODO: 'command: create pricing campaign' event not found in Ampli tracking plan - needs manual handling

									ampli.commandExecute({
										command_category: 'create pricing campaign',
									})
									runCommand(() => showModal('CREATE_PRICING_CAMPAIGN_MODAL'))
								}}
							>
								<PlusCircleIcon className='h-4 w-4' />
								Create pricing campaign
							</CommandMenuItem>
							<ResetOptimizationGroups
								onHighlight={() => setSelectedType('command')}
								onSelect={(command) => {
									ampli.commandExecute({
										command_category: 'reset optimization groups',
									})
									runCommand(command)
								}}
							/>
						</CommandGroup>
						{menuItems && menuItems.length > 0 && (
							<CommandGroup
								heading='Pages'
								className='!p-0 [&_[cmdk-group-heading]]:scroll-mt-16 [&_[cmdk-group-heading]]:!p-3 [&_[cmdk-group-heading]]:!pb-1'
							>
								{menuItems.map((item) => (
									<CommandMenuItem
										key={item.href}
										value={`Navigation ${item.label}`}
										keywords={['nav', 'navigation', item.label.toLowerCase()]}
										onHighlight={handlePageHighlight}
										onSelect={() => {
											runCommand(() => navigate({ to: item.href }))
										}}
									>
										<ArrowRightIcon />
										{item.label}
									</CommandMenuItem>
								))}
							</CommandGroup>
						)}

						{!!pricingCampaignsQuery.data?.length && (
							<CommandGroup heading='Pricing campaigns'>
								{pricingCampaignsQuery.data?.map((pc) => (
									<CommandMenuItem
										key={pc.id}
										value={`${pc.name} ${pc.optimization_group_id ? `(${pc.optimization_group_id})` : ''}`}
										keywords={[pc.name]}
										onHighlight={() => setSelectedType('pc')}
										onSelect={() => {
											ampli.commandExecute({
												command_category: 'pricing campaign navigate',
											})
											runCommand(() =>
												showModal('PRICING_CAMPAIGN_MODAL', {
													pricingCampaignId: pc.id,
												})
											)
										}}
									>
										<NetworkIcon className='h-4 w-4' />
										{pc.name}
										<EngineBadge
											className='ml-auto'
											engine={getPricingCampaignEngine(pc)}
										>
											{t('campaigns_om_title_' + getPricingCampaignEngine(pc))}
										</EngineBadge>
									</CommandMenuItem>
								))}
							</CommandGroup>
						)}
						{!!optimizationGroupsQuery.data?.length && (
							<CommandGroup heading='Optimization groups'>
								{optimizationGroupsQuery.data?.map((og) => (
									<CommandMenuItem
										key={og.id}
										value={og.name}
										keywords={[og.name]}
										onHighlight={() => setSelectedType('og')}
										onSelect={() => {
											ampli.commandExecute({
												command_category: 'optimization group navigate',
											})
											runCommand(() =>
												navigate({ to: `/p/og/${og.id}/products` })
											)
										}}
									>
										<CircleDollarSignIcon className='h-4 w-4' />
										{og.name}
									</CommandMenuItem>
								))}
							</CommandGroup>
						)}
						<CommandSeparator />
						<CommandGroup heading='Theme'>
							<CommandMenuItem
								onHighlight={() => setSelectedType('theme')}
								onSelect={() => runCommand(() => setTheme('light'))}
							>
								<SunIcon className='h-4 w-4' />
								Light
							</CommandMenuItem>
							<CommandMenuItem
								onSelect={() => runCommand(() => setTheme('dark'))}
							>
								<MoonIcon className='h-4 w-4' />
								Dark
							</CommandMenuItem>
							<CommandMenuItem
								onSelect={() => runCommand(() => setTheme('system'))}
							>
								<LaptopIcon className='h-4 w-4' />
								System
							</CommandMenuItem>
						</CommandGroup>
					</CommandList>
				</Command>
				<div className='absolute inset-x-0 bottom-0 z-20 flex h-10 items-center gap-2 rounded-b-xl border-t border-t-neutral-100 bg-neutral-50 px-4 text-xs font-medium text-muted-foreground dark:border-t-neutral-700 dark:bg-neutral-800'>
					<div className='flex items-center gap-2'>
						<CommandMenuKbd>
							<CornerDownLeftIcon />
						</CommandMenuKbd>{' '}
						{selectedType === 'page' ? 'Go to Page' : null}
						{selectedType === 'pc' ? 'Open Pricing Campaign' : null}
						{selectedType === 'og' ? 'Open Optimization Group' : null}
						{selectedType === 'command' ? 'Run Command' : null}
						{selectedType === 'theme' ? 'Set Theme' : null}
					</div>
					{copyPayload && (
						<>
							<Separator orientation='vertical' className='!h-4' />
							<div className='flex items-center gap-1'>
								<CommandMenuKbd>⌘</CommandMenuKbd>
								<CommandMenuKbd>C</CommandMenuKbd>
								{copyPayload}
							</div>
						</>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}

function CommandMenuKbd({ className, ...props }: React.ComponentProps<'kbd'>) {
	return (
		<kbd
			className={cn(
				"pointer-events-none flex h-5 items-center justify-center gap-1 rounded border bg-background px-1 font-sans text-[0.7rem] font-medium text-muted-foreground select-none [&_svg:not([class*='size-'])]:size-3",
				className
			)}
			{...props}
		/>
	)
}
